import { chain, get, isEmpty } from "lodash"
import { createSelector } from "reselect"
import { smallNumberFormatter } from "helpers/formatters/numbers"
import { getExcelValue } from "helpers/csv"
import { getSelectedDate } from "./index"
import { getBasketPositions, getRawSmartTradePositions, getBasketPostionsGMV, getSmartTradeCurrencyCode } from "./positions"

const buildHeaders = (currencyCode) => [
  "Identifier",
  "MIC",
  "Bloomberg Ticker",
  "Country",
  "Asset Class",
  "Currency",
  "Industry",
  `Trade Position Size (${currencyCode})`,
  "Trade Equity %",
]

const getInstrumentDescriptors = (state, props) => props.instrumentDescriptors
const getInstrumentPortfolio = (state, props) => props.instrumentPortfolio

const getFileLabel = createSelector(
  [getInstrumentDescriptors, getInstrumentPortfolio],
  (descriptors, portfolio) => {
    if (!isEmpty(portfolio)) { return portfolio.name }
    return get(descriptors, "ticker", "Unknown")
  },
)

export const getFileName = createSelector(
  [getFileLabel, getSelectedDate],
  (label, date) => `Smart Trade For ${label} - Trades ${date}`,
)

const getSmartSecurityCsvRows = createSelector(
  [getRawSmartTradePositions, getSmartTradeCurrencyCode],
  (contributors, currencyCode) => ([
    buildHeaders(currencyCode),
    ...chain(contributors)
      .sortBy((position) => get(position, "economicExposure"))
      .map(({ id, bloombergTicker, country, assetClass, classification, currency, dollarValue, economicExposure }) => {
        const { ticker, mic } = id ?? {}
        return [
          ticker,
          mic,
          bloombergTicker,
          country,
          assetClass,
          currency,
          get(classification, "name"),
          getExcelValue(dollarValue, { currency: true }),
          smallNumberFormatter(economicExposure),
        ]
      })
      .value(),
  ]),
)

const getSmartPortfolioCsvRows = createSelector(
  [getBasketPositions, getBasketPostionsGMV, getSmartTradeCurrencyCode],
  (basketPositions, basketPositionsGMV, currencyCode) => ([
    buildHeaders(currencyCode),
    ...chain(basketPositions)
      .sortBy(["id"])
      .map(({ id, bloombergTicker, country, assetClass, classification, currency, dollarValue }) => {
        const { ticker, mic } = id
        return [
          ticker,
          mic,
          bloombergTicker,
          country,
          assetClass,
          currency,
          get(classification, "name"),
          getExcelValue(dollarValue, { currency: true }),
          smallNumberFormatter(dollarValue / basketPositionsGMV * 100),
        ]
      })
      .value(),
  ]),
)

// Delegates to the memoized rows selector for the active mode only — the portfolio branch
// destructures position ids unguarded, so running it in security mode would throw.
export const getCsvRows = (state, props) => getInstrumentPortfolio(state, props)
  ? getSmartPortfolioCsvRows(state, props)
  : getSmartSecurityCsvRows(state, props)
